import { useCallback, useEffect, useState } from "react"
import { useConfigFlag } from "@cloudbreakus/featurebang-react"
import { dataService } from "utils/dataService"
import { logger } from "utils/logger"

export const useCurrentEncounter = ({ interactionId }) => {
  const [currentEncounterId, setCurrentEncounterId] = useState(null)
  const [currentEncounterSequenceNumber, setCurrentEncounterSequenceNumber] = useState(null)
  const [loading, setLoading] = useState(true)

  const multiPatientEnabled = useConfigFlag("multiPatient.enabled")
  const startNextPatientEnabled = useConfigFlag("multiPatient.startNextPatientEnabled")

  const fetchCurrentEncounter = useCallback(async () => {
    if (!interactionId) {
      setCurrentEncounterId(null)
      setCurrentEncounterSequenceNumber(null)
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const result = await dataService.IntakeQuestions.getCurrentEncounter({ interactionId })
      if (result?.data?.id) {
        setCurrentEncounterId(result.data.id)
      }
      if (result?.data?.sequenceNumber) {
        setCurrentEncounterSequenceNumber(result.data.sequenceNumber)
      }
    } catch (err) {
      logger.error(`Failed to fetch current encounter for interaction ${interactionId}`, err)
    } finally {
      setLoading(false)
    }
  }, [interactionId])

  useEffect(() => {
    fetchCurrentEncounter()
  }, [fetchCurrentEncounter])

  const canStartNextPatient = Boolean(multiPatientEnabled && startNextPatientEnabled)

  const startNextPatient = useCallback(async () => {
    if (!canStartNextPatient || !interactionId) return null
    setLoading(true)
    try {
      const result = await dataService.IntakeQuestions.advanceEncounter({ interactionId })
      if (result?.data?.id) setCurrentEncounterId(result.data.id)
      if (result?.data?.sequenceNumber) setCurrentEncounterSequenceNumber(result.data.sequenceNumber)
      return result?.data || null
    } catch (err) {
      logger.error(`Failed to advance encounter for interaction ${interactionId}`, err)
      return null
    } finally {
      setLoading(false)
    }
  }, [canStartNextPatient, interactionId])

  const isPastFirstEncounter = Boolean(currentEncounterSequenceNumber > 1)

  return {
    currentEncounterId,
    currentEncounterSequenceNumber,
    isPastFirstEncounter,
    canStartNextPatient,
    startNextPatient,
    loading,
    refetchEncounter: fetchCurrentEncounter,
  }
}
